import React, { useState, useEffect } from 'react';
import api from '../api';

const EXPIRY_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

export default function EmployerPortalManager({ shifts = [] }) {
  const http = api();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ label: '', shift_ids: [], expires_days: 7 });
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = async () => {
    try {
      const res = await http.get('/employer/client-portals');
      setPortals(res.data || []);
    } catch { setPortals([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { showToast('Please enter a portal label'); return; }
    setCreating(true);
    try {
      const res = await http.post('/employer/client-portal', form);
      showToast('Portal created! Link ready to share ✅');
      setShowModal(false);
      setForm({ label: '', shift_ids: [], expires_days: 7 });
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create portal');
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Revoke this portal? The link will stop working.')) return;
    setDeletingId(id);
    try {
      await http.delete(`/employer/client-portals/${id}`);
      showToast('Portal revoked');
      load();
    } catch { showToast('Failed to revoke'); }
    finally { setDeletingId(null); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => showToast('Link copied! 📋'));
  };

  const toggleShift = (shiftId) => {
    setForm(f => ({
      ...f,
      shift_ids: f.shift_ids.includes(shiftId)
        ? f.shift_ids.filter(id => id !== shiftId)
        : [...f.shift_ids, shiftId]
    }));
  };

  const isExpired = (expiresAt) => new Date(expiresAt) < new Date();

  return (
    <div className="animate-fade-in">
      {toast && (
        <div className="mb-4 p-3 rounded-xl text-sm bg-brand-50 border border-brand-200 text-brand-700">{toast}</div>
      )}

      {/* Create button */}
      <button
        onClick={() => setShowModal(true)}
        className="w-full mb-5 py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl text-sm transition shadow-sm"
      >
        + Create New Portal
      </button>

      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5">
        <p className="text-xs text-blue-700 font-medium mb-1">What is a Client Portal?</p>
        <p className="text-xs text-blue-600">Share a secure link with your clients so they can track shift status, worker attendance, and hours — without needing a ShiftMatch account.</p>
      </div>

      {/* Portals list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : portals.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-2xl border border-neutral-100 shadow-sm">
          <div className="text-4xl mb-3">🔗</div>
          <p className="font-medium text-neutral-600 mb-1">No portals yet</p>
          <p className="text-sm text-neutral-400">Create one to share live shift updates with your clients</p>
        </div>
      ) : (
        <div className="space-y-3">
          {portals.map(portal => (
            <div
              key={portal.id}
              className={`bg-white rounded-2xl border shadow-sm p-4 ${isExpired(portal.expires_at) ? 'border-neutral-200 opacity-60' : 'border-neutral-100'}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-neutral-900 truncate">{portal.label}</p>
                    {isExpired(portal.expires_at) && (
                      <span className="text-[10px] px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded-full font-semibold">EXPIRED</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    Expires: {new Date(portal.expires_at).toLocaleDateString('en-AU')}
                    {' '}· {(portal.shift_ids || []).length} shift{(portal.shift_ids || []).length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(portal.id)}
                  disabled={deletingId === portal.id}
                  className="p-2 text-neutral-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                  title="Revoke portal"
                >
                  {deletingId === portal.id
                    ? <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                  }
                </button>
              </div>

              {!isExpired(portal.expires_at) && (
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={portal.portal_url || `https://shiftmatch.work/portal/${portal.token}`}
                    className="flex-1 text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-2 text-neutral-600 truncate"
                  />
                  <button
                    onClick={() => copyLink(portal.portal_url || `https://shiftmatch.work/portal/${portal.token}`)}
                    className="px-3 py-2 bg-brand-500 text-white text-xs font-bold rounded-lg hover:bg-brand-600 transition flex-shrink-0"
                  >
                    Copy
                  </button>
                </div>
              )}
              {!isExpired(portal.expires_at) && (
                <p className="text-[10px] text-neutral-400 mt-2 text-center">Share link with client → they can view shift status without logging in</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-neutral-900 text-lg">Create Client Portal</h3>
                <button onClick={() => setShowModal(false)} className="p-1 rounded-lg hover:bg-neutral-100 transition">
                  <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-600 mb-1">Portal Label *</label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. Westfield Nov 8 Event"
                    className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-600 mb-1">Expiry</label>
                  <select
                    value={form.expires_days}
                    onChange={e => setForm(f => ({ ...f, expires_days: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
                  >
                    {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {shifts.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-2">Include Shifts (optional)</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {shifts.map(shift => (
                        <label key={shift.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.shift_ids.includes(String(shift.id))}
                            onChange={() => toggleShift(String(shift.id))}
                            className="w-4 h-4 text-brand-500 rounded"
                          />
                          <span className="text-sm text-neutral-700 flex-1 truncate">{shift.title}</span>
                          <span className="text-xs text-neutral-400">{shift.location}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button type="submit" disabled={creating}
                    className="flex-1 py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl text-sm transition disabled:opacity-50">
                    {creating ? 'Creating...' : 'Create Portal 🔗'}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)}
                    className="px-4 py-3 border border-neutral-200 text-neutral-600 rounded-xl text-sm hover:bg-neutral-50 transition">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
